import { Link } from 'react-router-dom';
import { DataIcon, GraphIcon, EquationIcon } from '../components/icons';
import styles from './Tools.module.css';

const TOOLS = [
  {
    name: 'Data Export',
    icon: DataIcon,
    description: 'Export experiment data in CSV, JSON, or HDF5 formats. Configure sampling rates, select variables, and include metadata for reproducibility.',
    features: [
      'Multi-format export (CSV, JSON, HDF5, NetCDF)',
      'Configurable sampling intervals',
      'Metadata inclusion (parameters, timestamps, version)',
      'Batch export for multiple runs',
      'Direct integration with Python/R/MATLAB workflows',
    ],
    status: 'available',
  },
  {
    name: 'Graph Analysis',
    icon: GraphIcon,
    description: 'Analyze data relationships with curve fitting, statistical summaries, and correlation analysis. Built for rigorous data interpretation.',
    features: [
      'Linear and non-linear curve fitting',
      'Residual analysis and goodness-of-fit metrics',
      'Statistical summaries (mean, std, confidence intervals)',
      'Correlation and covariance matrices',
      'Exportable analysis reports',
    ],
    status: 'available',
  },
  {
    name: 'Parameter Sweep',
    icon: EquationIcon,
    description: 'Systematically explore parameter space. Configure ranges, step sizes, and run multiple simulations to understand system behavior.',
    features: [
      'Multi-dimensional parameter sweeps',
      'Linear and logarithmic spacing',
      'Parallel execution for large sweeps',
      'Heatmap and surface visualization',
      'Identify critical points and bifurcations',
    ],
    status: 'beta',
  },
  {
    name: 'Reference Calculators',
    icon: EquationIcon,
    description: 'Quick access to physical constants, unit conversions, and mathematical reference tables. All values sourced from CODATA and NIST.',
    features: [
      'Physical constants (CODATA recommended values)',
      'Unit conversion with uncertainty propagation',
      'Mathematical formulas and identities',
      'Periodic table with isotopic data',
      'Material properties database',
    ],
    status: 'available',
  },
  {
    name: 'Uncertainty Calculator',
    icon: DataIcon,
    description: 'Propagate measurement uncertainties through calculations. First-order error propagation with Monte Carlo validation.',
    features: [
      'First-order error propagation formulas',
      'Monte Carlo uncertainty sampling',
      'Correlated variable handling',
      'Significant figure recommendations',
      'Detailed uncertainty reports',
    ],
    status: 'beta',
  },
  {
    name: 'Notebook Integration',
    icon: GraphIcon,
    description: 'Embed AxLabs simulations in Jupyter notebooks. Programmatic control over experiments and data retrieval.',
    features: [
      'Python API for experiment control',
      'Direct data retrieval to pandas/numpy',
      'Reproducible notebook workflows',
      'Batch experiment execution',
      'Integration with scientific computing stack',
    ],
    status: 'alpha',
  },
];

export function Tools() {
  return (
    <div className={styles.tools}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <Link to="/" className={styles.backLink}>← Back to Home</Link>
          <h1 className={styles.title}>Tools</h1>
          <p className={styles.subtitle}>
            Analysis utilities and productivity tools for working with simulations and experimental data.
            Designed for research workflows, not simplified for demonstration.
          </p>
        </div>
      </header>

      {/* Overview */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Research Tools</h2>
          <div className={styles.prose}>
            <p className={styles.proseText}>
              AxLabs provides tools that integrate with the simulation platform to support genuine
              scientific inquiry. Data export enables external analysis. Graph analysis helps identify
              patterns and validate models. Parameter sweeps reveal system behavior across conditions.
            </p>
            <p className={styles.proseText}>
              All tools prioritize reproducibility and transparency. Exported data includes complete
              metadata. Analysis methods are documented with assumptions. Calculations use verified
              constants from authoritative sources.
            </p>
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Available Tools</h2>
          <div className={styles.toolsGrid}>
            {TOOLS.map((tool) => {
              const IconComponent = tool.icon;
              return (
                <div key={tool.name} className={styles.toolCard}>
                  <div className={styles.toolHeader}>
                    <div className={styles.toolIcon}>
                      <IconComponent size={32} />
                    </div>
                    <div className={styles.toolMeta}>
                      <h3 className={styles.toolName}>{tool.name}</h3>
                      <span className={`${styles.toolStatus} ${styles[tool.status]}`}>
                        {tool.status === 'available' && 'Available'}
                        {tool.status === 'beta' && 'Beta'}
                        {tool.status === 'alpha' && 'Coming Soon'}
                      </span>
                    </div>
                  </div>
                  <p className={styles.toolDesc}>{tool.description}</p>
                  <ul className={styles.toolFeatures}>
                    {tool.features.map((feature) => (
                      <li key={feature} className={styles.toolFeature}>{feature}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Usage Philosophy */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Tool Philosophy</h2>
          <div className={styles.philosophyGrid}>
            <div className={styles.philosophyItem}>
              <h3 className={styles.philosophyTitle}>Reproducible Outputs</h3>
              <p className={styles.philosophyText}>
                Every tool produces documented, reproducible outputs. Parameters, timestamps, and
                version information are included automatically.
              </p>
            </div>
            <div className={styles.philosophyItem}>
              <h3 className={styles.philosophyTitle}>No Hidden Magic</h3>
              <p className={styles.philosophyText}>
                Analysis methods are documented with assumptions and limitations. Curve fitting
                algorithms report residuals and goodness-of-fit metrics.
              </p>
            </div>
            <div className={styles.philosophyItem}>
              <h3 className={styles.philosophyTitle}>Research-Grade Data</h3>
              <p className={styles.philosophyText}>
                Exported data preserves full precision. No arbitrary rounding or simplification.
                Appropriate for publication and serious analysis.
              </p>
            </div>
            <div className={styles.philosophyItem}>
              <h3 className={styles.philosophyTitle}>Integration-Focused</h3>
              <p className={styles.philosophyText}>
                Tools are designed to work with existing research workflows. Standard formats,
                programmatic access, and command-line interfaces where appropriate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Data Formats */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Data Export Formats</h2>
          <div className={styles.formatsGrid}>
            <div className={styles.formatCard}>
              <h3 className={styles.formatName}>CSV</h3>
              <p className={styles.formatDesc}>
                Comma-separated values for spreadsheet applications and simple analysis.
                Includes header row with variable names and units.
              </p>
            </div>
            <div className={styles.formatCard}>
              <h3 className={styles.formatName}>JSON</h3>
              <p className={styles.formatDesc}>
                Structured data with embedded metadata. Ideal for web applications
                and programmatic processing. Preserves data types and hierarchical structure.
              </p>
            </div>
            <div className={styles.formatCard}>
              <h3 className={styles.formatName}>HDF5</h3>
              <p className={styles.formatDesc}>
                Hierarchical Data Format for large datasets. Supports compression,
                chunking, and parallel I/O. Standard in scientific computing.
              </p>
            </div>
            <div className={styles.formatCard}>
              <h3 className={styles.formatName}>NetCDF</h3>
              <p className={styles.formatDesc}>
                Network Common Data Form for climate and atmospheric data.
                Self-describing with metadata. Widely used in earth sciences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Integration */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>External Integration</h2>
          <div className={styles.integrationList}>
            <div className={styles.integrationItem}>
              <h3 className={styles.integrationTitle}>Python</h3>
              <p className={styles.integrationText}>
                Direct integration with pandas, numpy, matplotlib, and scipy. Use exported data
                in Jupyter notebooks or standalone scripts.
              </p>
            </div>
            <div className={styles.integrationItem}>
              <h3 className={styles.integrationTitle}>R</h3>
              <p className={styles.integrationText}>
                Import data for statistical analysis with tidyverse. Compatible with RMarkdown
                for reproducible research documents.
              </p>
            </div>
            <div className={styles.integrationItem}>
              <h3 className={styles.integrationTitle}>MATLAB</h3>
              <p className={styles.integrationText}>
                Import HDF5 and CSV files directly. Use AxLabs data alongside existing
                MATLAB toolboxes and workflows.
              </p>
            </div>
            <div className={styles.integrationItem}>
              <h3 className={styles.integrationTitle}>Excel</h3>
              <p className={styles.integrationText}>
                CSV import with automatic column detection. Suitable for introductory
                data analysis and presentations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <Link to="/experiments" className={styles.footerLink}>Browse Experiments</Link>
          <span className={styles.footerSeparator}>·</span>
          <a href="https://github.com/staticpayload/axlabs" className={styles.footerLink} target="_blank" rel="noopener noreferrer">GitHub</a>
        </div>
      </footer>
    </div>
  );
}
